import React, { useEffect, useState } from "react";
import { Button, Flex, Tag } from "antd";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import AddContract from "./AddContract";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { STATUS_COLORS, STATUS_LABELS, canContracts, getContractType } from "./contractTypes";

/**
 * The contracts of one employee: on an employee's profile for the service provider, and
 * under "My Contracts" for the employee, where it is how they get to sign. The server limits
 * an employee to their own contracts either way.
 */
const ContractList = ({ employeeId, selfService }) => {
  const { t } = useTranslation("dashboard");
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/get`,
        { employee: employeeId, pageSize: 100 },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      setContracts(res.data.contracts || []);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [employeeId]);

  const columns = [
    {
      title: t("Title"),
      dataIndex: "title",
      mobile: "title",
      render: (title, record) => (
        <>
          <div>{title || t("Contract")}</div>
          <Tag color="geekblue">{t(getContractType(record.contractType).label)}</Tag>
        </>
      )
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate",
      render: date => (date ? dayjs(date).format("DD.MM.YYYY") : "-")
    },
    {
      title: t("End Date"),
      dataIndex: "endDate",
      render: date => (date ? dayjs(date).format("DD.MM.YYYY") : t("open-ended"))
    },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "subtitle",
      render: (value, record) => {
        const waitingForMe = selfService && !record.signatures?.employee;
        return (
          <Flex gap={4} wrap="wrap">
            <Tag color={STATUS_COLORS[value || "draft"]}>{t(STATUS_LABELS[value || "draft"])}</Tag>
            {waitingForMe && <Tag color="red">{t("Waiting for your signature")}</Tag>}
          </Flex>
        );
      }
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (_, record) => (
        <Link to={`/user/contract/${record._id}`}>
          <Button type={selfService && !record.signatures?.employee ? "primary" : "default"}>
            {selfService && !record.signatures?.employee ? t("Read and sign") : t("View")}
          </Button>
        </Link>
      )
    }
  ];

  return (
    <div className="contract-list">
      {!selfService && canContracts("Create") && (
        <Flex justify="flex-end" wrap="wrap" className="mb-2">
          <AddContract updateParent={load} presetEmployee={employeeId} />
        </Flex>
      )}
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={contracts}
        columns={columns}
        pagination={false}
        locale={{ emptyText: t("No contracts yet") }}
      />
    </div>
  );
};

export default ContractList;
